import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  TextInput,
  Pressable,
  FlatList,
  Linking,
  Text,
} from 'react-native';
import { Feather, FontAwesome } from '@expo/vector-icons';
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  onSnapshot,
  query,
  orderBy,
} from 'firebase/firestore';
import { firestore } from '../../services/firebase';
import useViewport from '../../hooks/useViewport';
import { useCustomTheme } from '../../contexts/useCustomTheme';
import { useAuthUser } from '../../contexts/useAuthUser';
import { StandardText, FooterText } from '../../config/theme/globalStyles';
import THEME from '../../config/theme';
import { ContainerMobile, ContainerDesktop, Content } from './style';
import ChatItem from './ChatItem';
import { Icon } from 'react-native-elements';

const isUrl = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.match(urlRegex);
};

const LiveStreamChat = ({ chat, videoId, categoryId, cardId }) => {
  const { width } = useViewport();
  const Container = width < 1080 ? ContainerMobile : ContainerDesktop;
  const { customColor, customFont } = useCustomTheme();

  // Sessão e documento `users/{uid}` vêm do AuthUserProvider.
  const { user: userDoc, userId, isLogged } = useAuthUser();
  const user = useMemo(
    () => (isLogged && userDoc ? { uid: userId, ...userDoc } : null),
    [isLogged, userDoc, userId]
  );
  const [chats, setChats] = useState([]);
  const [newChat, setNewChat] = useState('');
  const [pinnedChatId, setPinnedChatId] = useState(null);

  const chatsRef = collection(
    firestore,
    'categories',
    categoryId,
    'cards',
    cardId,
    'videos',
    videoId,
    'chats'
  );

  useEffect(() => {
    const q = query(chatsRef, orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      const pinned = data.find((d) => d.isPinned);
      setPinnedChatId(pinned?.id || null);
      setChats(data);
    });

    return () => unsubscribe();
  }, []);

  const sendChat = async () => {
    if (!user || !newChat.trim()) return;

    await addDoc(chatsRef, {
      chat: newChat,
      userId: user.uid,
      isAdmin: Boolean(user?.isAdmin),
      createdAt: new Date(),
      isPinned: false,
      userPhoto: user?.isAdmin ? "" : (user?.photo || user?.userPhoto || ""),
      userName: user?.Nome_Completo || ""
    });

    setNewChat('');
  };

  const togglePin = async (chatId) => {
    const currentPinnedRef = pinnedChatId
      ? doc(chatsRef, pinnedChatId)
      : null;

    if (currentPinnedRef) {
      await updateDoc(currentPinnedRef, { isPinned: false });
    }

    if (pinnedChatId !== chatId) {
      const newPinnedRef = doc(chatsRef, chatId);
      await updateDoc(newPinnedRef, { isPinned: true });
      setPinnedChatId(chatId);
    } else {
      setPinnedChatId(null);
    }
  };

  const renderChatText = (text) => {
    const parts = text.split(/(https?:\/\/[^\s]+)/g);
    return parts.map((part, index) =>
      isUrl(part) ? (
        <Text
          key={index}
          style={{
            color: customColor?.button?.textApp || THEME.COLORS.TEXT_BUTTON,
            textDecorationLine: 'underline'
          }}
          onPress={() => Linking.openURL(part)}
        >
          {part}
        </Text>
      ) : (
        <Text
          key={index}
          style={{
            color: customColor?.button?.textApp || THEME.COLORS.TEXT_BUTTON
          }}
        >
          {part}
        </Text>
      )
    );
  };

  return (
    <Container>
      <StandardText
        color={customColor?.text?.app || THEME.COLORS.TEXT_MAIN}
        style={{
          fontFamily: customFont?.fontFamilySubtitle || THEME.FONTFAMILY.MEDIUM,
          fontSize: customFont?.fontSM || THEME.FONTSIZE.SMALL,
        }}
        textAlign="left"
      >
        Chat ao vivo
      </StandardText>

      {pinnedChatId && (
        <>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Icon
              type="material-community"
              name="pin"
              size={THEME.FONTSIZE.SMALL}
              iconStyle={{
                color: customColor?.button?.backgroundApp || THEME.COLORS.PRIMARY_900,
                marginRight: 4,
              }}
            />
            <Text style={{ fontSize: 18, fontWeight: 'bold', color: customColor?.text?.app || THEME.COLORS.TITLE_MAIN }}>
              Comentário fixado
            </Text>
          </View>
          <View
            style={{
              padding: 10,
              backgroundColor: customColor?.button?.backgroundApp || THEME.COLORS.PRIMARY_900,
              borderRadius: 5,
              marginVertical: 8,
            }}
          >
            {renderChatText(
              chats.find((c) => c.id === pinnedChatId)?.chat || ''
            )}
          </View>
        </>
      )}

      <FlatList
        data={chats.filter((c) => c.id !== pinnedChatId)}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <ChatItem
            chat={item}
            categoryId={categoryId}
            cardId={cardId}
            videoId={videoId}
            pinnedChatId={pinnedChatId}
            user={user}
            togglePin={togglePin}
          />
        )}
      />

      {user && (
        <Content>
          <Feather
            name="user"
            size={22}
            color={customColor?.text?.app || THEME.COLORS.TEXT_MAIN}
          />
          <TextInput
            placeholder="Escreva algo..."
            value={newChat}
            onChangeText={setNewChat}
            placeholderTextColor="#7F7F7F"
            style={{
              backgroundColor: '#DBDBDB',
              color: '#7F7F7F',
              padding: 10,
              flex: 1,
              margin: 10,
              borderRadius: 5,
              fontFamily: customFont?.fontFamilyText || THEME.FONTFAMILY.MEDIUM,
            }}
          />
          <Pressable onPress={sendChat}>
            <FontAwesome
              name="send-o"
              size={18}
              color="white"
              style={{
                backgroundColor: customColor?.primary || THEME.COLORS.PRIMARY_900,
                padding: 10,
                borderRadius: 5,
              }}
            />
          </Pressable>
        </Content>
      )}
    </Container>
  );
};

export default LiveStreamChat;
